import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import RankBadge from './RankBadge';
import { avatarCatalog } from '../data/avatarCatalogV2';
import '../styles/UserProfile.css';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AVATAR_ICONS = [
  '👤', '🤴', '👸', '🦁', '🐉', '🦅', '⚔️', '🛡️', 
  '👑', '🎯', '🎨', '🎭', '🎪', '🎮', '🎲', '♟️',
  '🌟', '⭐', '💎', '🔥', '⚡', '🌙', '☀️', '🌈'
];

// Helper function to get avatar display (image or emoji)
const getAvatarDisplay = (avatarIdOrPath) => {
  if (!avatarIdOrPath) return { type: 'emoji', value: '👤' };
  
  // If it's already a path or data URL
  if (avatarIdOrPath.startsWith('/avatars') || 
      avatarIdOrPath.startsWith('data:image') || 
      avatarIdOrPath.startsWith('http')) {
    return { type: 'image', value: avatarIdOrPath };
  }
  
  // If it's an emoji character
  if (avatarIdOrPath.length < 10 && !avatarIdOrPath.includes('.')) {
    return { type: 'emoji', value: avatarIdOrPath };
  }
  
  // Look up the avatar in catalog by ID
  const avatar = avatarCatalog.find(av => av.id === avatarIdOrPath);
  if (avatar) {
    return { type: 'image', value: avatar.imagePath };
  }
  
  return { type: 'emoji', value: '👤' };
};

const UserProfile = ({ user, isCurrentPlayer, isPaid, isAI, isOpponent }) => {
  const { token } = useAuth();
  const [stats, setStats] = useState({ wins: 0, losses: 0, resignations: 0 });
  const [rank, setRank] = useState(null);

  useEffect(() => {
    // Only fetch stats for current user (not opponent)
    if (user && !isAI && !isOpponent && token) {
      fetchStats();
    }
  }, [user, token, isOpponent]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/profile/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setStats(response.data.stats);
      setRank(response.data.rank);
    } catch (error) {
      logger.error('Error fetching stats:', error);
    }
  };

  if (isAI) {
    return (
      <div className="user-profile ai-profile">
        <div className="profile-avatar ai-avatar">🤖</div>
        <div className="profile-info">
          <div className="profile-name">Chess AI</div>
          <div className="profile-status ai-status">Artificial Intelligence</div>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const avatarDisplay = getAvatarDisplay(user.avatar);
  const displayName = user.name || user.email?.split('@')[0] || 'Player';

  return (
    <div className={`user-profile ${isCurrentPlayer ? 'current-player' : ''} ${isOpponent ? 'opponent-profile' : ''}`}>
      <div className="profile-avatar">
        {avatarDisplay.type === 'image' ? (
          <img 
            src={avatarDisplay.value} 
            alt="Avatar"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: '50%'
            }}
          />
        ) : (
          avatarDisplay.value
        )}
        {isPaid && <div className="premium-badge" title="Premium User">⭐</div>}
      </div>
      <div className="profile-info">
        <div className="profile-header">
          <div className="profile-name">
            {isOpponent && <span className="opponent-label">VS </span>}
            {displayName}
          </div>
          {/* Show rank - use user.rank for opponents, local rank for current user */}
          {(isOpponent ? user.rank : rank) && (
            <RankBadge rank={isOpponent ? user.rank : rank} stats={isOpponent ? { wins: user.wins || 0 } : stats} size="small" />
          )}
        </div>
        <div className="profile-status">
          {isOpponent ? '🎯 Opponent' : (isPaid ? '👑 Premium' : '🆓 Free')}
        </div>
        {!isOpponent && (
          <div className="profile-stats">
            <span className="stat-item" title="Wins">🏆 {stats.wins}</span>
            <span className="stat-item" title="Losses">💔 {stats.losses}</span>
            <span className="stat-item" title="Resignations">🏳️ {stats.resignations}</span>
          </div>
        )}
        {isOpponent && user.wins !== undefined && (
          <div className="profile-stats">
            <span className="stat-item" title="Wins">🏆 {user.wins}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const AvatarSelector = ({ isOpen, onClose, onSelect, currentAvatar }) => {
  if (!isOpen) return null;

  return (
    <div className="avatar-selector-overlay" onClick={onClose}>
      <div className="avatar-selector-modal" onClick={(e) => e.stopPropagation()}>
        <div className="avatar-selector-header">
          <h3>Choose Your Avatar</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="avatar-grid">
          {AVATAR_ICONS.map((icon) => (
            <button
              key={icon}
              className={`avatar-option ${currentAvatar === icon ? 'selected' : ''}`}
              onClick={() => {
                onSelect(icon);
                onClose();
              }}
            >
              {icon}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export { UserProfile, AvatarSelector, AVATAR_ICONS };
export default UserProfile;
